import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { InputField } from '@enamelai/design-system'
import './AddressField.css'

export type ParsedAddress = {
  formattedAddress?: string | null
  formattedAddress2?: string | null
  postalCode?: string | null
  streetNumber?: string | null
  route?: string | null
  city?: string | null
  state?: string | null
  state2?: string | null
  country?: string | null
  countryCode?: string | null
  location?: { lat: number; lng: number } | null
  placeId?: string | null
}

export interface AddressFieldHandle {
  focus: () => void
  clear: () => void
}

export interface AddressFieldProps {
  label?: string
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  onSelect?: (
    place: google.maps.places.PlaceResult,
    parsed: ParsedAddress,
  ) => void
  placeholder?: string
  className?: string
  debounceMs?: number
  countries?: string[]
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
  listboxId?: string
  maxListHeight?: number
  detailFields?: string[]
  hint?: string
  destructive?: boolean
  /** Render with the design-system InputField and menu styles. */
  ds?: boolean
}

const AddressField = forwardRef<AddressFieldHandle, AddressFieldProps>(
  function AddressField(
    {
      label,
      value,
      defaultValue,
      onChange,
      onSelect,
      placeholder = 'Enter address',
      className,
      debounceMs = 150,
      countries,
      inputProps,
      listboxId = 'addr-suggestions',
      maxListHeight = 280,
      detailFields = [
        'formatted_address',
        'geometry.location',
        'address_components',
        'name',
        'place_id',
      ],
      hint,
      destructive,
      ds = false,
    },
    ref,
  ) {
    const placesLib = useMapsLibrary('places')

    const autocompleteService = useMemo(() => {
      if (!placesLib) return null
      return new placesLib.AutocompleteService()
    }, [placesLib])

    const placesService = useMemo(() => {
      if (!placesLib) return null
      return new placesLib.PlacesService(document.createElement('div'))
    }, [placesLib])

    const sessionTokenRef =
      useRef<google.maps.places.AutocompleteSessionToken | null>(null)

    useEffect(() => {
      if (placesLib) {
        sessionTokenRef.current = new placesLib.AutocompleteSessionToken()
      }
    }, [placesLib])

    const [internal, setInternal] = useState(defaultValue ?? '')
    const inputValue = value !== undefined ? value : internal

    const [predictions, setPredictions] = useState<
      google.maps.places.AutocompletePrediction[]
    >([])
    const [isOpen, setIsOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState(-1)

    const inputRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLUListElement>(null)
    const rootRef = useRef<HTMLDivElement>(null)
    const [isFocused, setIsFocused] = useState(false)

    // The DS address InputField does not forward its ref to the <input>.
    const getInput = (): HTMLInputElement | null =>
      ds ? (rootRef.current?.querySelector('input') ?? null) : inputRef.current

    const handleChange = (next: string): void => {
      if (value === undefined) setInternal(next)
      onChange?.(next)
    }

    useImperativeHandle(ref, () => ({
      focus: () => getInput()?.focus(),
      clear: () => {
        handleChange('')
        setPredictions([])
        setIsOpen(false)
        setActiveIndex(-1)
      },
    }))

    useEffect(() => {
      if (!autocompleteService || !sessionTokenRef.current) return

      if (!inputValue || !inputValue.trim()) {
        setPredictions([])
        setIsOpen(false)
        setActiveIndex(-1)
        return
      }

      const handle = setTimeout(
        () => {
          autocompleteService.getPlacePredictions(
            {
              input: inputValue,
              sessionToken: sessionTokenRef.current!,
              types: ['address'],
              componentRestrictions: countries
                ? { country: countries as any }
                : undefined,
            },
            (res) => {
              setPredictions(res ?? [])
              setIsOpen(Boolean(res?.length))
              setActiveIndex(res?.length ? 0 : -1)
            },
          )
        },
        Math.max(0, debounceMs),
      )

      return () => clearTimeout(handle)
    }, [inputValue, autocompleteService, countries, debounceMs])

    const parseAddress = (
      place: google.maps.places.PlaceResult,
    ): ParsedAddress => {
      const components = place.address_components ?? []
      const get = (type: string): any =>
        components.find((c) => c.types.includes(type))

      const postal = get('postal_code')
      const streetNumber = get('street_number')
      const route = get('route')
      const locality = get('locality')
      const sublocality = get('sublocality')
      const admin1 = get('administrative_area_level_1')
      const country = get('country')

      const loc = place.geometry?.location
        ? {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          }
        : null

      const formattedAddress2 = [streetNumber?.long_name, route?.long_name]
        .filter(Boolean)
        .join(' ')

      const state2 =
        admin1?.long_name && admin1?.short_name
          ? `${admin1.long_name} (${admin1.short_name})`
          : (admin1?.long_name ?? null)

      return {
        formattedAddress: place.formatted_address ?? null,
        formattedAddress2: formattedAddress2 || null,
        postalCode: postal?.long_name ?? null,
        streetNumber: streetNumber?.long_name ?? null,
        route: route?.long_name ?? null,
        city: locality?.long_name ?? sublocality?.long_name ?? null,
        state: admin1?.short_name ?? admin1?.long_name ?? null,
        state2: state2 ?? null,
        country: country?.long_name ?? null,
        countryCode: country?.short_name ?? null,
        location: loc,
        placeId: place.place_id ?? null,
      }
    }

    const choose = (
      p?: google.maps.places.AutocompletePrediction | null,
    ): void => {
      if (!p || !placesService || !sessionTokenRef.current) return

      placesService.getDetails(
        {
          placeId: p.place_id!,
          sessionToken: sessionTokenRef.current!,
          fields: detailFields,
        },
        (place, status) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK || !place)
            return

          const parsed = parseAddress(place)
          handleChange(place.formatted_address ?? p.description)
          setIsOpen(false)
          setPredictions([])
          setActiveIndex(-1)

          getInput()?.blur()

          sessionTokenRef.current = placesLib
            ? new placesLib.AutocompleteSessionToken()
            : new google.maps.places.AutocompleteSessionToken()

          onSelect?.(place, parsed)
        },
      )
    }

    const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
      if (!isOpen || !predictions.length) return

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => (i + 1) % predictions.length)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => (i - 1 + predictions.length) % predictions.length)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        choose(predictions[activeIndex] ?? predictions[0])
      } else if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }

    useEffect(() => {
      const onDocMouseDown = (ev: MouseEvent): void => {
        const t = ev.target as Node
        if (rootRef.current?.contains(t)) return
        setIsOpen(false)
      }
      document.addEventListener('mousedown', onDocMouseDown)
      return () => document.removeEventListener('mousedown', onDocMouseDown)
    }, [])

    const {
      disabled: inputDisabled,
      className: inputClassName,
      ...restInputProps
    } = inputProps ?? {}
    const isInputDisabled = Boolean(inputDisabled)
    const mergedInputClassName = [
      'input-field',
      destructive && 'input-field-destructive',
      isInputDisabled && 'input-field-disabled',
      inputClassName,
    ]
      .filter(Boolean)
      .join(' ')

    if (ds) {
      return (
        <div
          ref={rootRef}
          className={['address-field', className].filter(Boolean).join(' ')}
          onBlur={(e) => {
            if (rootRef.current?.contains(e.relatedTarget as Node | null))
              return
            setIsOpen(false)
            setIsFocused(false)
          }}
        >
          <InputField
            {...restInputProps}
            fullWidth
            variant="address"
            label={label}
            placeholder={placeholder}
            disabled={isInputDisabled}
            value={inputValue}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={onKeyDown}
            onFocus={() => {
              predictions.length && setIsOpen(true)
              setIsFocused(true)
            }}
            autoComplete="street-address"
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-expanded={isOpen}
            aria-activedescendant={
              activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined
            }
            error={destructive}
            helperText={hint}
          />

          {isOpen && predictions.length > 0 && isFocused && (
            <div className="ds-autocomplete address-field__suggestions">
              <div
                id={listboxId}
                className="ds-autocomplete__menu"
                role="listbox"
              >
                <div
                  className="ds-autocomplete__menu-scroll"
                  style={{ maxHeight: maxListHeight }}
                >
                  {predictions.map((p, i) => (
                    <div
                      key={p.place_id}
                      id={`${listboxId}-opt-${i}`}
                      role="option"
                      aria-selected={i === activeIndex}
                      className={`ds-autocomplete__option${
                        i === activeIndex
                          ? ' ds-autocomplete__option--selected'
                          : ''
                      }`}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        choose(p)
                      }}
                      onMouseEnter={() => setActiveIndex(i)}
                      title={p.description}
                    >
                      <span className="ds-autocomplete__option-label address-field__option-label">
                        {p.structured_formatting?.main_text ?? p.description}
                        {p.structured_formatting?.secondary_text && (
                          <span className="address-field__option-secondary">
                            {` ${p.structured_formatting.secondary_text}`}
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )
    }

    return (
      <div ref={rootRef} className={className} style={{ position: 'relative' }}>
        {label && <label className="form-label">{label}</label>}
        <input
          ref={inputRef}
          {...restInputProps}
          disabled={isInputDisabled}
          className={mergedInputClassName}
          value={inputValue}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => {
            predictions.length && setIsOpen(true)
            setIsFocused(true)
          }}
          onBlur={() => {
            setIsOpen(false)
            setIsFocused(false)
          }}
          placeholder={placeholder}
          autoComplete="street-address"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={isOpen}
          aria-activedescendant={
            activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined
          }
          style={{
            width: '100%',
            padding: '10px 12px',
            fontSize: 14,
            borderRadius: 8,
            outline: 'none',
            height: 32,
          }}
        />

        {isOpen && predictions.length > 0 && isFocused && (
          <ul
            id={listboxId}
            ref={listRef}
            role="listbox"
            style={{
              position: 'absolute',
              zIndex: 10,
              top: 'calc(100% + 6px)',
              left: 0,
              right: 0,
              margin: 0,
              padding: 8,
              listStyle: 'none',
              border: '1px solid #E5E7EB',
              borderRadius: 10,
              background: 'white',
              boxShadow: '0 8px 20px rgba(2,6,23,0.06)',
              maxHeight: maxListHeight,
              overflowY: 'auto',
              fontSize: 12,
            }}
          >
            {predictions.map((p, i) => (
              <li
                key={p.place_id}
                id={`${listboxId}-opt-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                onMouseDown={(e) => {
                  e.preventDefault()
                  choose(p)
                }}
                onMouseEnter={() => setActiveIndex(i)}
                title={p.description}
                style={{
                  padding: '10px 12px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: i === activeIndex ? '#F3F4F6' : 'transparent',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {p.structured_formatting?.main_text ?? p.description}
                <span style={{ color: '#6B7280' }}>
                  {' '}
                  {p.structured_formatting?.secondary_text
                    ? `— ${p.structured_formatting.secondary_text}`
                    : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
        {hint && <span className="hint-text">{hint}</span>}
      </div>
    )
  },
)

export default AddressField
